import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import TransitionWrapper from "../../../components/common/transition-wrapper";
import FadeInWrapper from "../../../components/common/fade-in-wrapper";
import { track } from "@amplitude/analytics-browser";
import BACK_IMG from "../../../components/common/back-img.png";
import { formatTimeWithAM } from "../../../utils/date/format-date";
import { notifyError } from "../../../utils/toast";

interface CheckoutPreOrderIntroProps {
  isLive?: boolean;
}

/** 여름철 음식 보관 안내 — 2026-09-23 23:59:59 KST까지 표기 */
const SUMMER_FOOD_NOTICE_END = new Date("2026-09-23T23:59:59+09:00");

function shouldShowSummerFoodNotice(now = new Date()): boolean {
  return now.getTime() <= SUMMER_FOOD_NOTICE_END.getTime();
}

const CheckoutPreOrderIntro = ({
  isLive = false,
}: CheckoutPreOrderIntroProps) => {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const time = searchParams.get("time");
  const isTomorrow = searchParams.get("isTomorrow") === "true";
  const reserveTime = searchParams.get("reserveTime");
  const closingHours = searchParams.get("closingHours");

  const [agreedGuide, setAgreedGuide] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 스크롤 초기화
  // 이전 페이지(스토어/팩 상세)의 스크롤 위치가 유지되어
  // 페이지 진입 시 최상단부터 보이지 않는 현상을 방지하기 위해 강제로 초기화함
  useEffect(() => {
    setTimeout(() => {
      containerRef.current?.scrollTo(0, 0);
      window.scrollTo(0, 0);
    }, 0);
  }, []);

  const reserveStart = reserveTime?.split(" ~ ")[0] || "";
  const reserveEnd = reserveTime?.split(" ~ ")[1] || "";

  const formattedConfirmTime = time ? formatTimeWithAM(time) : "";
  const formattedPickupStart = reserveStart
    ? formatTimeWithAM(reserveStart)
    : "";
  const formattedPickupEnd = reserveEnd ? formatTimeWithAM(reserveEnd) : "";
  const formattedClosingHours = closingHours
    ? formatTimeWithAM(closingHours)
    : "";

  const onClickButton = () => {
    if (agreedGuide && agreedPrivacy) {
      if (isLive) {
        track("실시간 팩 소개 네 확인했습니다 버튼 클릭", {});
        navigate("/live-order/checkout");
      } else {
        track("마감팩 소개 네 확인했습니다 버튼 클릭", {});
        navigate(`/pre-order/checkout/${storeId}?isTomorrow=${isTomorrow}`);
      }
    } else {
      notifyError("모든 안내 사항에 동의해주세요.");
    }
  };

  return (
    <div
      ref={containerRef}
      className="bg-white p-4 flex flex-col h-screen overflow-y-auto relative"
    >
      <FadeInWrapper className="flex flex-col pb-24">
        <img
          src={BACK_IMG}
          onClick={() => navigate(-1)}
          className="w-[40px] h-[40px] mb-4 cursor-pointer"
          alt="back"
        />
        <h1 className="text-3xl text-black font-extrabold mb-8">
          꼭 확인해 주세요
        </h1>

        {shouldShowSummerFoodNotice() && (
          <div className="mb-8 rounded-xl border border-[#F5D08A] bg-[#FFF8E8] px-4 py-3.5">
            <div className="flex gap-2.5">
              <span className="text-xl leading-none mt-0.5" aria-hidden>
                ☀️
              </span>
              <div className="flex flex-col gap-1.5 min-w-0">
                <p className="font-bold text-[15px] text-black leading-snug">
                  여름철에는 음식 보관에 주의해 주세요
                </p>
                <ul className="text-sm text-gray-700 space-y-0.5 list-disc pl-4">
                  <li>픽업 후 가급적 바로 섭취해 주세요</li>
                  <li>바로 드시지 않을 경우 반드시 냉장 보관해 주세요</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col">
          {/* Item 1 */}
          <div className="flex gap-3 relative pb-8">
            <div className="absolute left-3 top-7 -bottom-1 w-[2px] bg-gray-200 -ml-[1px]" />
            <div className="z-10 flex-shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center font-bold text-sm mt-1">
              1
            </div>
            <div className="flex flex-col">
              {isLive ? (
                <>
                  <p className="font-bold text-lg text-black">
                    결제 완료 즉시{" "}
                    <span className="text-[#1562fc]">주문이 확정</span>되어
                    취소가 불가능해요
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    실시간 마감 상품은 결제와 동시에 주문이 확정돼요
                    <br />
                    결제 완료 후에는 취소 및 환불이 어려워요
                  </p>
                </>
              ) : (
                <>
                  <p className="font-bold text-lg text-black">
                    예약은{" "}
                    <span className="text-[#1562fc]">
                      {isTomorrow ? "내일" : "오늘"} {formattedConfirmTime}
                    </span>{" "}
                    에 확정돼요
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    그 전까지는 매장 재고에 따라 예약이 취소될 수 있어요
                    <br />
                    예약이 확정되면 취소할 수 없어요
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Item 2 */}
          <div className="flex gap-3 relative pb-8">
            <div className="absolute left-3 top-7 -bottom-1 w-[2px] bg-gray-200 -ml-[1px]" />
            <div className="z-10 flex-shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center font-bold text-sm mt-1">
              2
            </div>
            <div className="flex flex-col w-full">
              {isLive ? (
                <>
                  <p className="font-bold text-lg text-black">
                    시간이 지나면{" "}
                    <span className="text-[#1562fc]">상품을 드릴 수 없어요</span>
                  </p>
                  {formattedClosingHours && (
                    <div className="bg-gray-100 rounded-lg p-3 mt-2 w-full text-center">
                      <span className="text-black font-medium">
                        이 매장의 마감 시간 : {formattedClosingHours}
                      </span>
                    </div>
                  )}
                  <p className="text-gray-500 text-sm mt-2">
                    선택하신 픽업 시간에 맞춰 꼭 매장에 방문해 주세요
                  </p>
                </>
              ) : (
                <>
                  <p className="font-bold text-lg text-black">
                    <span className="text-[#1562fc]">{formattedPickupStart}</span>{" "}
                    이전에는 픽업을 할 수 없어요
                  </p>
                  <div className="bg-gray-100 rounded-lg p-3 mt-2 w-full text-center">
                    <span className="text-black font-medium">
                      이 매장의 픽업 시간 : {formattedPickupStart} -{" "}
                      {formattedPickupEnd}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Item 3 */}
          <div className="flex gap-3 relative pb-8">
            <div className="absolute left-3 top-7 -bottom-1 w-[2px] bg-gray-200 -ml-[1px]" />
            <div className="z-10 flex-shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center font-bold text-sm mt-1">
              3
            </div>
            <div className="flex flex-col">
              <p className="font-bold text-lg text-black">
                픽업시간과 랜덤팩 이용 규칙을 꼭 지켜주세요
              </p>
              <ul className="text-gray-500 text-sm mt-1 list-disc pl-4 space-y-0.5">
                <li>이른·늦은 픽업은 안돼요.</li>
                <li>특정 메뉴·교환 요청 안돼요.</li>
                <li>
                  이용 규칙을 지키지 않을 경우 서비스 이용이 제한될 수 있어요.
                </li>
              </ul>
            </div>
          </div>

          {/* Item 4 */}
          <div className="flex gap-3 relative">
            <div className="z-10 flex-shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center font-bold text-sm mt-1">
              4
            </div>
            <div className="flex flex-col">
              {isLive ? (
                <>
                  <p className="font-bold text-lg text-black">
                    매장 방문 후 픽업 시 주문 내역을 보여주세요
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    주문 내역은 주문내역에서 확인해요
                  </p>
                </>
              ) : (
                <>
                  <p className="font-bold text-lg text-black">
                    매장 방문 후 픽업 시 예약 내역을 보여주세요
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    예약 내역은 주문내역에서 확인해요
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col space-y-3">
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="guide-agree"
              className="w-5 h-5 mt-0.5 rounded border-gray-300 text-[#1562fc] focus:ring-[#1562fc]"
              checked={agreedGuide}
              onChange={(e) => setAgreedGuide(e.target.checked)}
            />
            <label
              htmlFor="guide-agree"
              className="text-black text-md cursor-pointer select-none"
            >
              [필수] 안내 사항을 꼼꼼히 읽고 이해했어요
            </label>
          </div>
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="privacy-agree"
              className="w-5 h-5 mt-0.5 rounded border-gray-300 text-[#1562fc] focus:ring-[#1562fc]"
              checked={agreedPrivacy}
              onChange={(e) => setAgreedPrivacy(e.target.checked)}
            />
            <div className="flex flex-col">
              <label
                htmlFor="privacy-agree"
                className="text-black text-md cursor-pointer select-none flex items-center"
              >
                [필수] 개인정보 제3자 동의{" "}
                <a
                  href="https://subsequent-frog.super.site/29608e6e11b3800e8d73f172bb194d07"
                  target="_blank"
                  rel="noreferrer"
                  className="ml-1 text-gray-400 font-normal"
                >
                  {">"}
                </a>
              </label>
              <p className="text-gray-400 text-sm mt-0.5">
                {isLive
                  ? "주문 및 픽업 안내를 위해 매장에서 연락드릴 수 있어요"
                  : "예약 확정을 위해 매장에서 먼저 연락드릴 수 있어요"}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-6">
          {isLive ? (
            <>
              <div className="flex flex-col gap-2">
                <h2 className="text-lg font-bold text-black">
                  수령 및 방문 안내
                </h2>
                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                  <li>
                    결제 완료 후 선택한 픽업 시간에 맞춰 매장에 방문해 주세요.
                  </li>
                  <li>
                    매장 마감 시간 이전에 방문하지 않으면 제품 수령이
                    불가합니다.
                  </li>
                  <li>
                    픽업 시 주문 내역을 매장에 보여주시면 제품을 수령할 수
                    있어요.
                  </li>
                  <li>
                    마감히어로는 구매자 본인 수령을 원칙으로 운영합니다.
                  </li>
                </ul>
              </div>
              <div className="flex flex-col gap-2">
                <h2 className="text-lg font-bold text-black">
                  주문 취소 및 환불 안내
                </h2>
                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                  <li>
                    실시간 마감 상품은 결제와 동시에 주문이 확정됩니다.
                  </li>
                  <li>
                    결제 완료 후에는 취소 및 환불이 어렵습니다.
                  </li>
                  <li>
                    제품 정보 및 수량 변경은 불가합니다.
                  </li>
                  <li>
                    픽업 시간이 늦어 제품을 수령하지 못한 경우, 취소 및 환불이
                    불가합니다.
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <h2 className="text-lg font-bold text-black">예약 및 방문 안내</h2>
                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                  <li>예약은 픽업 시간 기준 30분 전에 확정됩니다.</li>
                  <li>매장 사정에 따라 예약이 취소될 수 있습니다.</li>
                  <li>
                    확정 알림을 받으면, 픽업 시간에 맞춰 매장에 방문해주세요.
                  </li>
                  <li>픽업시간 이전, 이후 방문은 제품 수령이 불가합니다.</li>
                  <li>
                    마감히어로는 구매자 본인 수령을 원칙으로 운영합니다.
                  </li>
                </ul>
              </div>
              <div className="flex flex-col gap-2">
                <h2 className="text-lg font-bold text-black">예약 취소 안내</h2>
                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                  <li>
                    예약이 확정되면 '픽업 대기' 표시가 되고, 알림을 받아요.
                  </li>
                  <li>
                    예약이 확정되기 전에는 자유롭게 취소할 수 있으며, 결제
                    금액은 100% 환불됩니다.
                  </li>
                  <li>예약이 확정된 이후에는 취소/환불이 불가합니다.</li>
                  <li>
                    픽업 시간이 늦어 제품을 수령하지 못한 경우, 취소 및 환불이
                    불가합니다.
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      </FadeInWrapper>

      <TransitionWrapper
        scale={0.9}
        opacity={0.8}
        onClick={onClickButton}
        className={`w-full h-[60px] fixed bottom-6 left-0 right-0 mx-auto max-w-[calc(100%-2rem)] flex items-center justify-center rounded-3xl border transition-colors ${
          agreedGuide && agreedPrivacy
            ? "bg-[#1562fc] border-transparent"
            : "bg-gray-300 border-gray-300"
        }`}
      >
        <div className="text-center flex items-center space-x-1">
          <span className="text-white font-bold leading-snug">
            네 확인했습니다!
          </span>
        </div>
      </TransitionWrapper>
    </div>
  );
};

export default CheckoutPreOrderIntro;
